@layer refine.base {
  :host {
    --_font-size: var(--textarea-font-size, var(--text-sm));
    --_gap: var(--textarea-gap, var(--size-2));
    --_padding: var(--textarea-padding, var(--size-2) var(--size-3));
    --_radius: var(--textarea-radius, var(--rounded-lg));
    --_placeholder: var(--textarea-placeholder-color, var(--color-contrast-500));
    --_bg: var(--textarea-bg, var(--color-contrast-100));
    --_border-color: var(--textarea-border-color, var(--color-contrast-300));
    --_min-height: var(--textarea-min-height, var(--size-24));
    --_max-height: var(--textarea-max-height, none);
    --_resize: var(--textarea-resize, vertical);

    display: inline-flex;
    flex-direction: column;
    align-items: stretch;
    min-width: 12rem;
  }

  .textarea-wrapper {
    display: flex;
    flex-direction: column;
    gap: var(--size-1);
    min-width: 0; /* Allow shrinking below content size */
  }

  /* Inset placement (default): label positioned inside field border */
  :host(:not([label-placement='outside'])) .textarea-wrapper {
    position: relative;
  }

  :host(:not([label-placement='outside'])) .label {
    position: absolute;
    inset-block-start: var(--size-2);
    inset-inline-start: var(--_padding-x, var(--size-3));
    z-index: 1;
    pointer-events: none;
  }

  :host(:not([label-placement='outside'])) .textarea-wrapper:has(.label:not([hidden])) .field {
    padding-block-start: calc(var(--size-2) + 1lh + 2px);
  }

  .field {
    position: relative;
    box-sizing: border-box;
    display: grid; /* Grid automatically sizes to content */
    min-width: 0; /* Allow shrinking */
    padding: var(--_padding);
    background: var(--_bg);
    border: var(--border) solid var(--_border-color);
    border-radius: var(--_radius);
    box-shadow: var(--shadow-2xs);
    transition:
      background var(--transition-fast),
      backdrop-filter var(--transition-fast),
      border-color var(--transition-fast),
      box-shadow var(--transition-fast);
  }

  /* ========================================
       Status Icon & Loading Spinner (top-right corner — clear of the
       bottom-right native resize handle)
       ======================================== */

  .status-icon,
  .field-spinner {
    position: absolute;
    inset-block-start: var(--size-2);
    inset-inline-end: var(--size-2);
  }

  .status-icon {
    display: none;
    align-items: center;
    justify-content: center;
    width: var(--size-5);
    height: var(--size-5);
  }

  :host([error]:not([error=''])) .status-icon,
  :host([success]) .status-icon {
    display: flex;
  }

  .status-icon[data-status='error'] {
    color: var(--color-error);
  }

  .status-icon[data-status='success'] {
    color: var(--color-success);
  }

  /* Keyframe must live in shadow DOM — @keyframes don't inherit across shadow roots */
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .field-spinner {
    display: none;
    width: var(--size-4);
    height: var(--size-4);
    border: var(--border-2) solid var(--_theme-focus, currentcolor);
    border-inline-end-color: transparent;
    border-radius: 50%;
    animation: var(--_motion-animation, spin var(--transition-loader) infinite);
  }

  :host([loading]) .field-spinner {
    display: inline-block;
  }

  /* ========================================
       Label Styles
       ======================================== */

  .label,
  label.label {
    /* Default: inset style */
    margin-bottom: 2px;
    font-size: var(--text-xs);
    font-weight: var(--font-medium);
    line-height: var(--leading-tight);
    color: var(--color-contrast-500);
    cursor: pointer;
    user-select: none;
    transition: color var(--transition-fast);
  }

  /* Outside placement overrides */
  :host([label-placement='outside']) .label,
  :host([label-placement='outside']) label.label {
    margin-bottom: 0;
    font-size: var(--text-sm);
    line-height: var(--leading-none);
  }

  /* ========================================
       Helper / Error Text
       ======================================== */

  .helper-text[hidden] {
    display: none;
  }

  .helper-text {
    padding-inline: 2px;
    font-size: var(--text-xs);
    line-height: var(--leading-tight);
    color: color-mix(in oklch, var(--color-contrast-500) 80%, var(--color-canvas));
  }

  /* ========================================
       Textarea Element
       ======================================== */

  textarea {
    box-sizing: border-box;
    min-width: 12rem; /* Match :host min-width to prevent resize below minimum */
    min-height: var(--_min-height);
    max-height: var(--_max-height);
    padding: 0;
    font: inherit;
    font-size: var(--_font-size);
    line-height: var(--leading-relaxed);
    color: var(--textarea-color, var(--text-color-body));
    resize: vertical; /* Default resize, can be overridden by inline style */
    outline: none;
    background: transparent;
    border: none;
  }

  textarea::placeholder {
    color: var(--_placeholder);
    transition: color var(--transition-fast);
  }

  textarea:focus-visible {
    outline: none;
  }

  /* ========================================
       Character Counter
       ======================================== */

  .counter {
    align-self: flex-end;
    padding-inline: 2px;
    margin-top: var(--size-1);
    font-size: var(--text-xs);
    line-height: var(--leading-tight);
    color: var(--color-contrast-400);
    transition: color var(--transition-fast);
  }

  .counter.near-limit {
    color: var(--color-warning);
  }

  .counter.at-limit {
    color: var(--color-error);
  }

  /* ========================================
       Hover & Focus States
       ======================================== */

  :host(:not([disabled], [variant='bordered'], [variant='flat'])) .field:hover {
    border-color: var(--color-contrast-400);
  }

  :host(:not([disabled])) .field:focus-within ~ .label,
  :host(:not([disabled])) .textarea-wrapper:has(.field:focus-within) .label {
    color: var(--_theme-focus);
  }

  /* Error State */
  :host([error]:not([error=''])) .field {
    border-color: var(--color-error);
  }

  :host([error]:not([error=''])) .label {
    color: var(--color-error);
  }

  /* Success State */
  :host([success]) .field {
    border-color: var(--color-success);
  }
}

/*
 * Focus/error/success `box-shadow` on `.field` — moved out of `@layer refine.base` into
 * `refine.variants` (alongside `fieldVariantMixin`'s rest-state `box-shadow` for solid/outline/
 * ghost) so normal specificity decides the winner. Cascade layers beat specificity outright, so
 * a `refine.base` box-shadow here always lost to every variant's unconditional rest-state
 * `box-shadow`, focused/error/success or not — the same bug class fixed for `ore-otp-input`'s
 * focus ring.
 */
@layer refine.variants {
  :host(:not([disabled], [variant='flat'])) .field:focus-within {
    background: var(--color-canvas);
    border-color: var(--_theme-focus);
    box-shadow: var(--_theme-shadow, var(--color-primary-focus-shadow));
  }

  :host([error]:not([error=''])) .field:focus-within {
    border-color: var(--color-error);
    box-shadow: var(--color-error-focus-shadow);
  }

  :host([success]) .field:focus-within {
    border-color: var(--color-success);
    box-shadow: var(--color-success-focus-shadow);
  }
}

/*
 * The solid/flat/bordered/outline/ghost variant switch (previously here) now lives in
 * `fieldVariantMixin({ container: '.field', text: 'textarea', tokenPrefix: 'textarea' })`
 * in `textarea.ts`'s `styles` array — shared with `ore-input`/`ore-message-composer` instead of
 * hand-duplicated across all three (see the mixin's own doc comment for what it covers and why).
 */

@layer refine.utilities {
  /* Auto-resize hides overflow */
  :host([auto-resize]) textarea {
    overflow: hidden;
  }

  /* Full width */
  :host([fullwidth]) {
    width: 100%;
  }
}

/* ========================================
     Disabled State — intentionally unlayered
     ======================================== */

/*
 * The shared `disabledLoadingMixin` (pulled in via `fieldMixins`) dims `:host([disabled])`
 * with `opacity: 0.5` as bare, unlayered CSS. Cascade layers always lose to unlayered rules
 * regardless of source order or specificity, so overriding that from inside any `@layer`
 * block in this file (including `refine.overrides`) would be a no-op — this has to stay
 * unlayered too, to actually win. See `ore-input`'s `input.css` for the same pattern.
 *
 * Per the field-state UX review: opacity-based dimming reads as a washed-out/loading look
 * and can mimic reduced-contrast content for low-vision users, rather than a clearly locked
 * field. Swapped for an explicit grayscale surface + `not-allowed` cursor (the cursor already
 * comes from the shared mixin, left untouched here).
 */
:host([disabled]) {
  opacity: 1;
}

:host([disabled]) .field {
  background: var(--color-contrast-100);
  border-color: var(--color-contrast-200);
  box-shadow: none;
}

:host([disabled]) textarea {
  color: var(--color-contrast-400);
}

:host([disabled]) textarea::placeholder {
  color: var(--color-contrast-300);
}

:host([disabled]) .label {
  color: var(--color-contrast-400);
}
